// src/components/cases/CaseListItem.js
import React, { useState, useEffect } from 'react';
import { Trash2, Archive, TriangleAlert } from 'lucide-react';
import { ReactComponent as TagCreate } from '../../assets/icons/tag-create.svg';
import { ReactComponent as Restore } from '../../assets/icons/restore.svg';
import AnimatedBriefcase from './AnimatedBriefcase';
import DocumentSetSidebarList from '../document-sets/DocumentSetSidebarList';
import DocumentSetInlineEditor from '../document-sets/DocumentSetInlineEditor';
import DeleteAlertDialog from '../ui/DeleteAlertDialog';
import { incrementActionCount } from '../../hooks/useGuide';
import { useAuth } from '../../firebase/AuthContext';
import { useGuideForce } from '../../contexts/GuideForceContext';

/**
 * Individual Action Button Component
 */
const ActionButton = ({ icon: Icon, onClick, variant = 'default', title, forceHovered = false, 'data-guide-target': dataGuideTarget }) => {
  const [isHovered, setIsHovered] = useState(false);

  const effectiveHovered = isHovered || forceHovered;

  const buttonClasses = "w-comp-xl h-comp-xl rounded-app-xs transition-all duration-app-normal flex items-center justify-center";

  const backgroundClasses = variant === 'delete'
    ? (effectiveHovered ? 'bg-red-200' : '')
    : variant === 'add'
    ? (effectiveHovered ? 'bg-green-200' : '')
    : (effectiveHovered ? 'bg-app-gray-400' : '');

  const iconColorClasses = variant === 'delete'
    ? (effectiveHovered ? 'text-red-600' : 'text-app-gray-700')
    : variant === 'add'
    ? (effectiveHovered ? 'text-green-600' : 'text-app-gray-700')
    : 'text-app-gray-700';

  return (
    <button
      className={`${buttonClasses} ${backgroundClasses}`}
      onClick={onClick}
      title={title}
      data-guide-target={dataGuideTarget}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Icon className={`w-comp-md h-comp-md transition-colors duration-app-normal ${iconColorClasses}`} />
    </button>
  );
};

/**
 * Case List Item Component
 */
const CaseListItem = ({
  caseItem,
  isSelected,
  onSelect,
  onDelete,
  onArchive,
  onAddToSet,
  collapsed = false,
  onModalStateChange,
  documentSets = [],
  selectedSetId = null,
  onSelectSet,
  onToggleLockSet,
  onDeleteSet,
  onCreateDocumentSet,
  onUpdateSetName,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreatingSet, setIsCreatingSet] = useState(false);

  const { currentUser } = useAuth();
  const { tagGuideStep } = useGuideForce();
  const guideForceActions = tagGuideStep === 2 && isSelected;

  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isDeleteDialogOpen || isCreatingSet);
    }
  }, [isDeleteDialogOpen, isCreatingSet, onModalStateChange]);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleteDialogOpen(true);
  };

  const handleArchiveToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onArchive();
  };

  const handleTagClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsCreatingSet(true);
  };

  const handleCreateSet = async (name, color) => {
    if (!onCreateDocumentSet) return;
    await onCreateDocumentSet(name, color);
    setIsCreatingSet(false);
    if (currentUser?.uid) {
      await incrementActionCount(currentUser.uid, 'createTag');
      await incrementActionCount(currentUser.uid, 'createTagInMainFlow');
    }
  };

  const handleCancelCreate = () => {
    setIsCreatingSet(false);
  };

  const handleSelect = (e) => {
    if (e.target.closest('button')) return;
    onSelect();
  };

  if (collapsed) {
    return (
      <div
        className={`h-comp-5xl px-app-sm flex items-center justify-center cursor-pointer rounded-app-sm ${
          isSelected ? 'bg-app-gray-300' : 'hover:bg-app-gray-300'
        }`}
        onClick={handleSelect}
        title={caseItem.name}
      >
        <AnimatedBriefcase isOpen={isSelected} className="text-app-gray-600" />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Case item row */}
        <div
          className={`h-comp-5xl px-app-xs flex items-center cursor-pointer rounded-app-sm ${
            isSelected || isHovered ? 'bg-app-gray-300' : ''
          }`}
          onClick={handleSelect}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <AnimatedBriefcase
            isOpen={isSelected}
            className="w-comp-lg h-comp-lg text-app-gray-600 m-app-xs flex-shrink-0"
          />

          <div
            className={`flex-1 min-w-0 ml-app-md text-app-sm truncate transition-all duration-200 ${
              isSelected ? 'font-semibold text-app-gray-900' : 'font-normal text-app-gray-600'
            }`}
          >
            {caseItem.name}
          </div>

          {/* Action buttons — visible when hovered */}
          {(isHovered || guideForceActions) && (
            <div className="flex items-center flex-shrink-0">
              <ActionButton
                icon={TagCreate}
                onClick={handleTagClick}
                variant="add"
                title="Create tag"
                data-guide-target={isSelected ? 'tag-guide-create-tag' : undefined}
              />
              <ActionButton
                icon={caseItem.archived ? Restore : Archive}
                onClick={handleArchiveToggle}
                variant="default"
                title={caseItem.archived ? "Reactivate case" : "Archive case"}
              />
              <ActionButton
                icon={Trash2}
                onClick={handleDeleteClick}
                variant="delete"
                title="Delete case"
              />
            </div>
          )}
        </div>

        {/* Document sets / inline editor / empty state guide */}
        {isSelected && (
          <div className="mt-1">
            {isCreatingSet && (
              <div className="px-app-xs mb-[2px]">
                <DocumentSetInlineEditor
                  onCreateSet={handleCreateSet}
                  onCancel={handleCancelCreate}
                  documentSets={documentSets}
                />
              </div>
            )}

            {(documentSets.length > 0 || (tagGuideStep === 4 && isSelected)) ? (
              <DocumentSetSidebarList
                documentSets={documentSets}
                selectedSetId={selectedSetId}
                onSelectSet={onSelectSet}
                onToggleArchive={onToggleLockSet}
                onDeleteSet={onDeleteSet}
                onUpdateSetName={onUpdateSetName}
                isVisible={isSelected}
              />
            ) : null}

          </div>
        )}
      </div>

      <DeleteAlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={
          <div className="flex items-center gap-4 min-w-0">
            <span className="flex-shrink-0">Deleting case:</span>
            <span className="inline-block px-app-sm py-app-xs bg-app-gray-400 border border-app-gray-800 rounded-app-sm font-medium text-app-sm text-app-gray-800 not-italic truncate min-w-0">
              {caseItem.name}
            </span>
          </div>
        }
        warnings={[
          <>This will <strong className="text-app-gray-700">also delete</strong> all documents in this case. This can't be undone.</>
        ]}
        showBullets={false}
        warningIcon={<TriangleAlert className="w-comp-5xl h-comp-5xl" />}
        warningIconColor="text-app-warning"
        confirmButtonText="Delete case and docs"
        confirmButtonIcon={<Trash2 className="w-comp-lg h-comp-lg" />}
        onConfirm={onDelete}
      />
    </>
  );
};

export default CaseListItem;